import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { translate } from '../utils/translator';
import CostBreakdownModal from './CostBreakdown/CostBreakDownModal';
import { ORDER_FLOWS, SWITZERLAND } from '../utils/consts';
import { useCartContext } from '../customHooks/useCart';
import { track as trackGTM } from '../PixelTrackers/GtmTracker';


type Props = {
    flow?: string | null;
    sampleType?: string | null;
    isCarePlus?: boolean;
    isCannabis?: boolean;
}

const CartViewFAQ: React.FC<Props> = ({ flow, sampleType, isCarePlus = false, isCannabis = false }) => {
    const isTestLab = !!sampleType;
    const { language, country } = useParams();
    const { getCart } = useCartContext();
    const cart = getCart();
    const { getSessionFields } = useCartContext();
    const { mention } = getSessionFields();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMenuItem, setSelectedMenuItem] = useState("why-choose");

    const isSchengenFlow = flow === ORDER_FLOWS.SCHENGEN;
    const isOtcOnly = flow === ORDER_FLOWS.OTC_ONLY;

    // Shown wherever the DE-style price breakdown is enabled (DE/CH always; FR when subsidized).
    if (!['DE', 'CH', 'FR'].includes(country?.toUpperCase() || "")) {
        return null;
    }

    // Check if we're in self-pickup flow
    const isSelfPickupFlow = cart?.selected_service_option === 'self_pickup';
    const isOnlyPrescription = isSelfPickupFlow && !!cart?.only_prescription;

    // Test lab FAQ items - shown when isTestLab is true; CH has its own copy under the `_ch` keys
    const keySuffix = country?.toUpperCase() === SWITZERLAND ? `_${SWITZERLAND.toLowerCase()}` : '';
    const testLabFaqItems = [
        {
            id: "test-lab-how-it-works",
            title: translate(language, "default", `CartViewFAQ/test_lab_how_it_works${keySuffix}`),
        },
        {
            id: "test-lab-results-time",
            title: translate(language, "default", `CartViewFAQ/test_lab_results_time${keySuffix}`),
        },
        {
            id: "test-lab-what-to-do",
            title: translate(language, "default", `CartViewFAQ/test_lab_what_to_do${keySuffix}`),
        },
    ];

    // Default FAQ items - shown when isTestLab is false
    const defaultFaqItems = [
        {
            id: "why-choose",
            title: translate(language, "default", "CartViewFAQ/why_choose_doktorabc"),
        },
        {
            id: "total-amount",
            title: translate(language, "default", "CartViewFAQ/will_total_increase"),
        },
        ...(!isSchengenFlow && !isOtcOnly ? [{
            id: "prescription-approval",
            title: translate(language, "default", "CartViewFAQ/what_if_not_approved"),
        }] : []),
        ...(isSelfPickupFlow ? [{
            id: "self-pickup",
            title: translate(language, "default", "CartViewFAQ/self_pickup"),
        }] : [])
    ];

    // Select which FAQ items to display
    const faqItems = isTestLab ? testLabFaqItems : defaultFaqItems;

    const handleFAQClick = (index: number) => {
        const menuItemId = faqItems[index].id;
        const questionText = faqItems[index].title;
        
        // Track FAQ click in GTM
        trackGTM("custom", "ga4_auto_event", {
            'event_name': 'FAQ_payment',
            'question': questionText,
            'subcategory': mention
            // category will be auto-added by GtmTracker based on subcategory
        });
        
        setSelectedMenuItem(menuItemId);
        setIsModalOpen(true);
    };


    return (
        <div className="mt-5">
            {/* FAQ Items */}
            {faqItems.map((item, index) => (
                <div key={index} className="flex flex-col mt-2 mb-2">
                    <div
                        onClick={() => handleFAQClick(index)}
                        className="w-full cursor-pointer px-4 py-3 text-left flex justify-between items-center hover:bg-gray-50"
                    >
                        <span className="font-roboto text-[16px] font-normal text-[#656D79] w-[90%]">{item.title}</span>
                        {/* Care+ uses the orange accent (Figma Weight Loss); every other flow keeps the green. */}
                        {isCarePlus ? (
                            <span
                                className="font-roboto text-[18px] font-medium bg-clip-text text-transparent"
                                style={{ backgroundImage: "linear-gradient(222.69deg, #ffb909 0.8%, #e17200 94.83%)" }}
                            >
                                +
                            </span>
                        ) : (
                            <span className="font-roboto text-[18px] font-medium text-text-success">+</span>
                        )}
                    </div>
                    <hr color='#E5E5E5' />
                </div>
            ))}

            <CostBreakdownModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                language={language}
                country={country}
                initialSelectedMenuItem={selectedMenuItem}
                flow={flow}
                sampleType={sampleType}
                isSelfPickup={isSelfPickupFlow}
                isOnlyPrescription={isOnlyPrescription}
                isCannabis={isCannabis}
            />
        </div>
    );
};

export default CartViewFAQ; 